import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { useFeatureToggle } from '~/platform/utilities/feature-toggles';

import {
  buildSafeDateFormatter,
  claimDetailHref,
  generateClaimTitle,
  getClaimPhaseTypeHeaderText,
  getFailedSubmissionsWithinLast30Days,
  getListItemLabel,
  getShowEightPhases,
  getStatusDescription,
  isChampvaProviderClaim,
} from '../utils';

import CardLink from './CardLink';

const formatDate = buildSafeDateFormatter();

function CommunicationsLine({ children, icon }) {
  return (
    <div className="vads-u-display--flex vads-u-align-items--center">
      <va-icon
        icon={icon}
        size={3}
        class="vads-u-margin-right--1"
        aria-hidden="true"
      />
      <p className="vads-u-margin--0">{children}</p>
    </div>
  );
}

CommunicationsLine.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.string.isRequired,
};

export default function ClaimContent({ claim, onLinkClick }) {
  const {
    claimDate,
    claimPhaseDates,
    claimTypeCode,
    claimStatusMeta,
    decisionLetterSent,
    documentsNeeded,
    status,
    vesStatusUpdatedDate,
    evidenceSubmissions = [],
  } = claim.attributes || {};

  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const cstClaimPhasesEnabled = useToggleValue(TOGGLE_NAMES.cstClaimPhases);
  const cstMultiClaimProviderEnabled = useToggleValue(
    TOGGLE_NAMES.cstMultiClaimProvider,
  );
  const cstClaimDetailsVaTabsEnabled = useToggleValue(
    TOGGLE_NAMES.cstClaimDetailsVaTabs,
  );
  const showEightPhases = getShowEightPhases(
    claimTypeCode,
    cstClaimPhasesEnabled,
  );

  const listCardMeta = claimStatusMeta?.listCard || {};
  const { decisionLetterLabel } = listCardMeta;
  const stepContent = showEightPhases
    ? claimStatusMeta?.whatWeAreDoing?.phaseTypeMap?.[
        claimPhaseDates?.phaseType
      ]
    : claimStatusMeta?.whatWeAreDoing?.statusMap?.[status];

  const isClaimComplete =
    claim.attributes.status?.toLowerCase() === 'complete' ||
    claim.attributes.status === 'vbms';
  const showPrecomms = !decisionLetterSent && !isClaimComplete;
  const showAlert = showPrecomms && documentsNeeded;

  const failedSubmissionsWithinLast30Days = useMemo(
    () => getFailedSubmissionsWithinLast30Days(evidenceSubmissions),
    [evidenceSubmissions],
  );
  const showActionTag =
    showAlert || failedSubmissionsWithinLast30Days.length > 0;

  const humanStatus =
    stepContent?.title ||
    (showEightPhases
      ? getClaimPhaseTypeHeaderText(claimPhaseDates?.phaseType)
      : getStatusDescription(status));

  const phaseChangeDate = claimPhaseDates?.phaseChangeDate;
  const currentStep = claimStatusMeta?.overview?.currentStepByStatus?.[status];
  // Use vesStatusUpdatedDate only when the claim is in Step 2 (decided); fall back to
  // phaseChangeDate for Step 1 or if the field is set before a phase revert.
  const updatedOn = formatDate(
    currentStep === 2 && vesStatusUpdatedDate
      ? vesStatusUpdatedDate
      : phaseChangeDate,
  );
  const lastUpdatedPrefix =
    listCardMeta.lastUpdatedPrefix || 'Moved to this step on';
  const lastUpdated = updatedOn ? `${lastUpdatedPrefix} ${updatedOn}` : null;

  const provider = cstMultiClaimProviderEnabled
    ? claim.attributes?.provider
    : null;
  const showDecisionLetter =
    decisionLetterSent &&
    (!isChampvaProviderClaim(claim) || decisionLetterLabel);

  const formattedReceiptDate = formatDate(claimDate);
  const hasReceiptDate = Boolean(formattedReceiptDate);
  const cardTitle = listCardMeta.title || generateClaimTitle(claim);
  const receivedLabel = listCardMeta.receivedLabel || 'Received on';
  const label = getListItemLabel(claim);
  const ariaLabel = hasReceiptDate
    ? `Details for ${cardTitle} submitted on ${formattedReceiptDate}`
    : `Details for ${cardTitle}`;
  const href = claimDetailHref(claim.id, {
    provider,
    useVaTabs: cstClaimDetailsVaTabsEnabled,
  });

  return (
    <>
      <h3 className="claim-list-item-header vads-u-margin-bottom--2">
        <div>
          {label && (
            <>
              <span className="usa-label">{label}</span>{' '}
            </>
          )}
          {cardTitle}
          {hasReceiptDate && (
            <span className="vads-u-margin-top--0p5 submitted-on">
              {`${receivedLabel} ${formattedReceiptDate}`}
            </span>
          )}
        </div>
      </h3>
      {showDecisionLetter && (
        <CommunicationsLine icon="mail">
          {decisionLetterLabel || 'You have a decision letter ready'}
        </CommunicationsLine>
      )}
      <div className="card-status">
        {humanStatus && <p>{humanStatus}</p>}
        {lastUpdated && <p>{lastUpdated}</p>}
      </div>
      {showActionTag && (
        <va-tag-status status="warning" text="Action may be needed" />
      )}
      <CardLink ariaLabel={ariaLabel} href={href} onLinkClick={onLinkClick} />
    </>
  );
}

ClaimContent.propTypes = {
  claim: PropTypes.object.isRequired,
  onLinkClick: PropTypes.func,
};
